import React, { useState, useRef, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useInspectionOperations } from "../../../hooks/vehicle/useInspectionOperations";
import { useSnackbar } from "notistack";
import {
  Tabs,
  Tab,
  MenuItem,
  Popover,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import {
  getVehicleTypeCategory,
  REQUIRED_PLATES,
} from "../../../utils/vehicle/vehicleUtils.js";
import "../../../assets/scss/components/ui/list/InspectionList.scss";

// Definición de campos para vehículos
const VEHICLE_FIELDS = [
  { id: "plate", label: "Placa", sticky: true, type: "text", width: "100px" },
  { id: "vehicle_type", label: "Tipo", type: "text" },
  { id: "brand", label: "Marca", type: "text" },
  { id: "model", label: "Modelo", type: "text", width: "80px" },
  { id: "color", label: "Color", type: "text" },
  { id: "tonnage", label: "Tonelaje", type: "text", width: "80px" },
  { id: "service_type", label: "Servicio", type: "text" },
  { id: "owner", label: "Propietario", type: "text" },
  { id: "owner_document", label: "Doc. Propietario", type: "text" },
  { id: "trailer_plate", label: "Placa Trailer", type: "text", width: "100px" },
  { id: "trailer_model", label: "Modelo Trailer", type: "text", width: "80px" },
  // Campo compuesto para mostrar el nombre del operador
  { 
    id: "operator", 
    label: "Operador", 
    type: "composite", 
    fields: ["operator_first_names", "operator_last_names"] 
  },
  // Campos con fechas de expiración
  { 
    id: "soat", 
    label: "SOAT", 
    type: "expiry", 
    numberField: "soat_number", 
    expiryField: "soat_expiry" 
  },
  { 
    id: "technical_review", 
    label: "Técnico-Mecánica", 
    type: "expiry", 
    numberField: "technical_review", 
    expiryField: "technical_review_expiry" 
  },
  { 
    id: "preventive_review", 
    label: "Rev. Preventiva", 
    type: "expiry", 
    numberField: "preventive_review", 
    expiryField: "preventive_review_expiry" 
  },
  { 
    id: "contractual_insurance", 
    label: "Póliza Contractual", 
    type: "expiry", 
    numberField: "contractual_insurance", 
    expiryField: "contractual_insurance_expiry" 
  },
  { 
    id: "extracontractual_insurance", 
    label: "Póliza Extracontractual", 
    type: "expiry", 
    numberField: "extracontractual_insurance", 
    expiryField: "extracontractual_insurance_expiry" 
  },
  { 
    id: "hydrocarbons_policy", 
    label: "Póliza Hidrocarburos", 
    type: "expiry", 
    numberField: "hydrocarbons_policy", 
    expiryField: "hydrocarbons_policy_expiry" 
  },
  { 
    id: "lifeline_certification", 
    label: "Cert. Línea de Vida", 
    type: "expiry", 
    numberField: "lifeline_certification", 
    expiryField: "lifeline_certification_expiry" 
  },
  { 
    id: "gauge_table", 
    label: "Tabla de Aforo", 
    type: "expiry", 
    numberField: "gauge_table", 
    expiryField: "gauge_table_expiry" 
  },
  { 
    id: "king_pin_certification", 
    label: "King Pin", 
    type: "expiry", 
    numberField: "king_pin_certification", 
    expiryField: "king_pin_certification_expiry" 
  },
  { 
    id: "fifth_wheel_certification", 
    label: "Quinta Rueda", 
    type: "expiry", 
    numberField: "fifth_wheel_certification", 
    expiryField: "fifth_wheel_certification_expiry" 
  },
  { 
    id: "hydrostatic_test", 
    label: "Prueba Hidrostática", 
    type: "expiry", 
    numberField: "hydrostatic_test", 
    expiryField: "hydrostatic_test_expiry" 
  },
  { 
    id: "hydrostatic_five_years_test", 
    label: "Prueba Hidrostática 5 Años", 
    type: "expiry", 
    numberField: "hydrostatic_five_years_test", 
    expiryField: "hydrostatic_five_years_test_expiry" 
  },
  { 
    id: "thickness_test", 
    label: "Prueba de Espesores", 
    type: "expiry", 
    numberField: "thickness_test", 
    expiryField: "thickness_test_expiry" 
  },
  { 
    id: "andina_policy", 
    label: "Póliza Andina", 
    type: "expiry", 
    numberField: "andina_policy", 
    expiryField: "andina_policy_expiry" 
  },
  { 
    id: "trailer_suitability_card", 
    label: "Tarj. Idoneidad Trailer", 
    type: "expiry", 
    numberField: "trailer_suitability_card", 
    expiryField: "trailer_suitability_card_expiry" 
  },
  { 
    id: "cabin_suitability_card", 
    label: "Tarj. Idoneidad Cabina", 
    type: "expiry", 
    numberField: "cabin_suitability_card", 
    expiryField: "cabin_suitability_card_expiry" 
  },
  { 
    id: "ecuador_gauge_table", 
    label: "Tabla Aforo Ecuador", 
    type: "expiry", 
    numberField: "ecuador_gauge_table", 
    expiryField: "ecuador_gauge_table_expiry" 
  },
  { 
    id: "service_provision_permit", 
    label: "Permiso Provisión Servicio", 
    type: "expiry", 
    numberField: "service_provision_permit", 
    expiryField: "service_provision_permit_expiry" 
  },
  { id: "contractor_company", label: "Contratista", type: "text" },
  { id: "contractor_nit", label: "NIT Contratista", type: "text" },
  { id: "is_own", label: "Es Propio", type: "boolean" },
  { id: "is_third", label: "Es Tercero", type: "boolean" },
  { id: "gps_system", label: "Sistema GPS", type: "text" },
  { id: "emergency_card", label: "Tarjeta Emergencia", type: "boolean" },
  { id: "safety_data_sheet", label: "Hoja Seguridad", type: "boolean" },
  { id: "updated_at", label: "Último Actualizado", type: "date", readonly: true }
];

const InspectionList = ({ theme }) => {
  // Estado para el tipo de entidad
  const [entityType, setEntityType] = useState("vehicles");

  // Estado local
  const [pagination, setPagination] = useState({ page: 1, limit: 10 });
  const [filters, setFilters] = useState({
    plate: "",
    operator_id: "",
    name: "",
    vehicleType: "", // Filtro para tipo de vehículo
  });
  const [isEditingId, setIsEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [useRequiredPlates, setUseRequiredPlates] = useState(false); 
  // Estado para el menú de exportación
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const exportBtnRef = useRef(null);

  // Hooks y servicios
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const inspectionOperations = useInspectionOperations();

  // Consulta para obtener entidades
  const {
    data: entitiesData,
    isLoading,
    isError,
    error,
  } = inspectionOperations.useEntities(
    {
      ...filters,
      page: pagination.page,
      limit: pagination.limit,
      requiredPlates: useRequiredPlates ? "true" : undefined, // Pasamos este parámetro al backend
    },
    entityType
  );

  // Mutaciones
  const updateEntityMutation = inspectionOperations.useUpdateEntity();
  const deleteEntityMutation = inspectionOperations.useDeleteEntity();
  const exportToExcelMutation = inspectionOperations.useExportToExcel();

  // Extraer datos
  const entities = entitiesData?.data || [];
  const paginationInfo = entitiesData?.pagination || { total: 0, pages: 0 };

  // Manejar cambio de tipo de entidad
  const handleTypeChange = (_, newType) => {
    setEntityType(newType);
    setPagination({ page: 1, limit: 10 });
    setSelectedItems([]);
    setSelectAll(false);
    setIsEditingId(null);
    setEditData({});
    setUseRequiredPlates(false);
    setFilters((prev) => ({
      plate: "",
      operator_id: "",
      name: "",
      vehicleType: "",
    }));
  };

  // Manejar cambios en filtros
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  // Aplicar filtros
  const handleApplyFilters = (e) => {
    e.preventDefault();
    // La consulta se actualiza automáticamente gracias a las dependencias del hook
  };

  // Manejar el cambio en el checkbox de placas requeridas
  const handleRequiredPlatesChange = (e) => {
    setUseRequiredPlates(e.target.checked);
    setPagination((prev) => ({ ...prev, page: 1 }));

    // Si se activa el filtro de placas requeridas, limpiamos el filtro manual de placa
    if (e.target.checked) {
      setFilters((prev) => ({ ...prev, plate: "" }));
    }
  };

  // Cambiar página
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= paginationInfo.pages) {
      setPagination((prev) => ({ ...prev, page: newPage }));
    }
  };

  // Iniciar edición
  const handleEdit = (entity) => {
    setIsEditingId(entity.id);
    setEditData({ ...entity });
  };

  // Cancelar edición
  const handleCancelEdit = () => {
    setIsEditingId(null);
    setEditData({});
  };

  // Actualizar cambios
  const handleSaveEdit = async () => {
    try {
      await updateEntityMutation.mutateAsync({
        id: isEditingId,
        data: editData,
        type: entityType === "operators" ? "operator" : "vehicle",
      });

      // Cerrar modo edición
      setIsEditingId(null);
      setEditData({});
    } catch (err) {
      enqueueSnackbar("Error al guardar cambios", { variant: "error" });
    }
  };

  // Manejar cambios en edición
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditData((prev) => ({ ...prev, [name]: value }));
  };

  // Eliminar entidad
  const handleDelete = async (id) => {
    const entityName = entityType === "operators" ? "operador" : "vehículo";
    if (window.confirm(`¿Está seguro de eliminar este ${entityName}?`)) {
      try {
        await deleteEntityMutation.mutateAsync({
          id,
          type: entityType === "operators" ? "operator" : "vehicle",
        });
      } catch (err) {
        enqueueSnackbar(`Error al eliminar el ${entityName}`, {
          variant: "error",
        });
      }
    }
  };

  // Funciones para manejar selecciones
  const handleItemSelection = (id) => {
    setSelectedItems((prev) =>
      prev.includes(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id]
    );

    // Actualizar estado de selectAll si es necesario
    if (selectAll && !selectedItems.includes(id)) {
      setSelectAll(false);
    }
  };

  const handleSelectAllItems = () => {
    if (selectAll) {
      setSelectedItems([]);
    } else {
      const allIds =
        entityType === "operators"
          ? entities.map((op) => op.operator_id_number)
          : entities.map((veh) => veh.plate);
      setSelectedItems(allIds);
    }
    setSelectAll(!selectAll);
  };

  // Abrir menú de exportación
  const handleExportClick = (event) => {
    // Si hay elementos seleccionados, exportar directamente
    if (selectedItems.length > 0) {
      handleExport();
    } else {
      // Si no hay seleccionados, mostrar menú de opciones
      setExportMenuAnchor(event.currentTarget);
    }
  };

  // Cerrar menú de exportación
  const handleCloseExportMenu = () => {
    setExportMenuAnchor(null);
  };

  // Exportar a Excel con opciones
  const handleExport = async (exportType) => {
    try {
      handleCloseExportMenu();

      let exportPayload = {
        type: entityType,
        ...filters,
      };

      if (selectedItems.length > 0) {
        // Si hay elementos seleccionados, exportar solo esos
        if (entityType === "operators") {
          exportPayload.selectedOperatorIds = selectedItems;
        } else {
          exportPayload.selectedPlates = selectedItems;
        }
      } else if (exportType === "required") {
        // Exportar solo placas requeridas
        exportPayload.selectedPlates = REQUIRED_PLATES;
        exportPayload.exportType = "classified"; // Indicamos al backend que use clasificación especial
      } else if (exportType === "all") {
        // Exportar todo
        exportPayload.exportType = "all";
      }
      await exportToExcelMutation.mutateAsync(exportPayload);
    } catch (err) {
      enqueueSnackbar("Error al exportar a Excel", { variant: "error" });
    }
  };

  // Función para calcular días faltantes
  const calculateDaysRemaining = (expiryDate) => {
    if (!expiryDate) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const expiry = new Date(expiryDate);
    expiry.setHours(0, 0, 0, 0);

    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays;
  };

  // Función para obtener clase CSS según días restantes
  const getDaysRemainingClass = (days, theme) => {
    if (days === null) return `expiry-na-${theme}`;

    if (days < 0) return `expiry-expired-${theme}`;
    if (days <= 30) return `expiry-warning-${theme}`;
    if (days <= 60) return `expiry-alert-${theme}`;
    return `expiry-valid-${theme}`;
  };

  // Renderizar celda con fecha y días restantes
  const renderExpiryCell = (expiryDate, documentNumber = null) => {
    const daysRemaining = calculateDaysRemaining(expiryDate);
    const expiryClass = getDaysRemainingClass(daysRemaining, theme);

    return (
      <>
        <div>{documentNumber || "N/A"}</div>
        <div className={expiryClass}>{formatDate(expiryDate)}</div>
        {daysRemaining !== null && (
          <div className={`days-remaining ${expiryClass}`}>
            {daysRemaining >= 0
              ? `${daysRemaining} días`
              : `${Math.abs(daysRemaining)} días vencido`}
          </div>
        )}
      </>
    );
  };

  // Renderizar cabeceras de tabla para vehículos de forma dinámica
  const renderVehicleTableHeader = () => {
    return (
      <tr>
        <th className="sticky-column selection-column">
          <input
            type="checkbox"
            checked={selectAll}
            onChange={handleSelectAllItems}
            className={`checkbox-${theme}`}
          />
        </th>
        
        {VEHICLE_FIELDS.map(field => (
          <th 
            key={field.id} 
            className={field.sticky ? "sticky-column" : ""}
            style={field.width ? { width: field.width } : {}}
          >
            {field.label}
          </th>
        ))}
        
        <th className="sticky-column-right">Acciones</th>
      </tr>
    );
  };

  // Renderizar datos de vehículo en modo visualización
  const renderVehicleViewMode = (entity) => {
    return (
      <>
        <td className="sticky-column selection-column">
          <input
            type="checkbox"
            checked={selectedItems.includes(entity.plate)}
            onChange={() => handleItemSelection(entity.plate)}
            className={`checkbox-${theme}`}
          />
        </td>

        {VEHICLE_FIELDS.map(field => {
          switch (field.type) {
            case "text":
              return (
                <td key={field.id} className={field.sticky ? "sticky-column" : ""}>
                  {entity[field.id] || "N/A"}
                </td>
              );
            
            case "date":
              return (
                <td key={field.id}>{formatDate(entity[field.id])}</td>
              );
              
            case "boolean":
              return (
                <td key={field.id}>
                  {entity[field.id] === true ? "Sí" : entity[field.id] === false ? "No" : "N/A"}
                </td>
              );
              
            case "composite":
              // Para campos compuestos como nombres de operador
              return (
                <td key={field.id}>
                  {field.fields.map(f => entity[f] || "").filter(Boolean).join(" ") || "N/A"}
                </td>
              );
              
            case "expiry":
              // Para campos con fechas de expiración
              return (
                <td key={field.id}>
                  {renderExpiryCell(entity[field.expiryField], entity[field.numberField])}
                </td>
              );
              
            default:
              return <td key={field.id}>N/A</td>;
          }
        })}

        <td className="action-buttons sticky-column-right">
          <button
            className={`edit-button-${theme}`}
            onClick={() => handleEdit(entity)}
            disabled={!!isEditingId || deleteEntityMutation.isLoading}
          >
            Editar
          </button>
          <button
            className={`delete-button-${theme}`}
            onClick={() => handleDelete(entity.id)}
            disabled={!!isEditingId || deleteEntityMutation.isLoading}
          >
            {deleteEntityMutation.isLoading &&
            deleteEntityMutation.variables?.id === entity.id
              ? "Eliminando..."
              : "Eliminar"}
          </button>
        </td>
      </>
    );
  };

  // Renderizar datos de vehículo en modo edición
  const renderVehicleEditMode = (entity) => {
    return (
      <>
        <td className="sticky-column selection-column">
          <input
            type="checkbox"
            checked={selectedItems.includes(entity.plate)}
            disabled={true}
            className={`checkbox-${theme}`}
          />
        </td>

        {VEHICLE_FIELDS.map(field => {
          // No permitir edición para campos de solo lectura
          if (field.readonly) {
            return (
              <td key={field.id}>
                {field.type === "date" 
                  ? formatDate(entity[field.id]) 
                  : entity[field.id] || "N/A"}
              </td>
            );
          }

          switch (field.type) {
            case "text":
              return (
                <td key={field.id} className={field.sticky ? "sticky-column" : ""}>
                  <input
                    type="text"
                    name={field.id}
                    value={editData[field.id] || ""}
                    onChange={handleEditChange}
                    className={`edit-input-${theme}`}
                    placeholder={field.label}
                  />
                </td>
              );
              
            case "boolean":
              return (
                <td key={field.id}>
                  <select
                    name={field.id}
                    value={editData[field.id] === true ? "true" : editData[field.id] === false ? "false" : ""}
                    onChange={(e) => {
                      const value = e.target.value === "true" ? true : 
                                    e.target.value === "false" ? false : null;
                      setEditData(prev => ({ ...prev, [field.id]: value }));
                    }}
                    className={`edit-input-${theme}`}
                  >
                    <option value="">Seleccionar</option>
                    <option value="true">Sí</option>
                    <option value="false">No</option>
                  </select>
                </td>
              );
              
            case "composite":
              // Para campos compuestos como nombres de operador
              return (
                <td key={field.id}>
                  {field.fields.map((fieldName, index) => (
                    <input
                      key={fieldName}
                      type="text"
                      name={fieldName}
                      value={editData[fieldName] || ""}
                      onChange={handleEditChange}
                      className={`edit-input-${theme}`}
                      placeholder={index === 0 ? "Nombres" : "Apellidos"}
                      style={{ marginBottom: index === 0 ? "5px" : "0" }}
                    />
                  ))}
                </td>
              );
              
            case "expiry":
              // Para campos con fechas de expiración
              return (
                <td key={field.id}>
                  <input
                    type="text"
                    name={field.numberField}
                    value={editData[field.numberField] || ""}
                    onChange={handleEditChange}
                    className={`edit-input-${theme}`}
                    placeholder={`Número ${field.label}`}
                    style={{ marginBottom: "5px" }}
                  />
                  <input
                    type="date"
                    name={field.expiryField}
                    value={
                      editData[field.expiryField]
                        ? editData[field.expiryField].split("T")[0]
                        : ""
                    }
                    onChange={handleEditChange}
                    className={`edit-input-${theme}`}
                  />
                </td>
              );
              
            default:
              return <td key={field.id}>N/A</td>;
          }
        })}

        <td className="action-buttons sticky-column-right">
          <button
            className={`save-button-${theme}`}
            onClick={handleSaveEdit}
            disabled={updateEntityMutation.isLoading}
          >
            {updateEntityMutation.isLoading
              ? "Guardando..."
              : "Guardar"}
          </button>
          <button
            className={`cancel-button-${theme}`}
            onClick={handleCancelEdit}
            disabled={updateEntityMutation.isLoading}
          >
            Cancelar
          </button>
        </td>
      </>
    );
  };

  // Renderizar columnas por tipo
  const renderTableHeader = () => {
    if (entityType === "operators") {
      return (
        <tr>
          <th className="sticky-column selection-column">
            <input
              type="checkbox"
              checked={selectAll}
              onChange={handleSelectAllItems}
              className={`checkbox-${theme}`}
            />
          </th>
          <th className="sticky-column">ID</th>
          <th>Nombres</th>
          <th>Apellidos</th>
          <th>Licencia</th>
          <th>Categoría</th>
          <th>Vence Licencia</th>
          <th>ARL</th>
          <th>EPS</th>
          <th>Teléfono</th>
          <th>Vence Defensiva</th>
          <th>Vence P. Auxilios</th>
          <th>Vence S. Peligrosas</th>
          <th>Vence Médicos</th>
          <th>Último Actualizado</th>
          <th className="sticky-column-right">Acciones</th>
        </tr>
      );
    }
    
    return renderVehicleTableHeader();
  };

  // Renderizar filas por tipo
  const renderTableRows = () => {
    if (isLoading) {
      return (
        <tr className={`loading-row-${theme}`}>
          <td colSpan={entityType === "operators" ? "16" : (VEHICLE_FIELDS.length + 2)}>
            <div className="loading-indicator">
              <span className="spinner"></span> Cargando...
            </div>
          </td>
        </tr>
      );
    }

    if (entities.length === 0) {
      return (
        <tr className={`empty-row-${theme}`}>
          <td colSpan={entityType === "operators" ? "16" : (VEHICLE_FIELDS.length + 2)}>
            No se encontraron registros
          </td>
        </tr>
      );
    }

    return entities.map((entity) => {
      const isEditing = isEditingId === entity.id;

      if (entityType === "operators") {
        // Renderizar operador
        return (
          <tr
            key={entity.id}
            className={isEditing ? `editing-row-${theme}` : ""}
          >
            {isEditing ? (
              // Modo edición para operador
              <>
                <td className="sticky-column selection-column">
                  <input
                    type="checkbox"
                    checked={selectedItems.includes(entity.operator_id_number)}
                    disabled={true}
                    className={`checkbox-${theme}`}
                  />
                </td>

                <td className="sticky-column">{entity.operator_id_number}</td>

                <td>
                  <input
                    type="text"
                    name="operator_first_names"
                    value={editData.operator_first_names || ""}
                    onChange={handleEditChange}
                    className={`edit-input-${theme}`}
                  />
                </td>

                <td>
                  <input
                    type="text"
                    name="operator_last_names"
                    value={editData.operator_last_names || ""}
                    onChange={handleEditChange}
                    className={`edit-input-${theme}`}
                  />
                </td>

                <td>
                  <input
                    type="text"
                    name="operator_license"
                    value={editData.operator_license || ""}
                    onChange={handleEditChange}
                    className={`edit-input-${theme}`}
                  />
                </td>

                <td>
                  <input
                    type="text"
                    name="operator_license_category"
                    value={editData.operator_license_category || ""}
                    onChange={handleEditChange}
                    className={`edit-input-${theme}`}
                  />
                </td>

                <td>
                  <input
                    type="date"
                    name="operator_license_expiry"
                    value={
                      editData.operator_license_expiry
                        ? editData.operator_license_expiry.split("T")[0]
                        : ""
                    }
                    onChange={handleEditChange}
                    className={`edit-input-${theme}`}
                  />
                </td>

                <td>
                  <input
                    type="text"
                    name="operator_arl"
                    value={editData.operator_arl || ""}
                    onChange={handleEditChange}
                    className={`edit-input-${theme}`}
                  />
                </td>

                <td>
                  <input
                    type="text"
                    name="operator_eps"
                    value={editData.operator_eps || ""}
                    onChange={handleEditChange}
                    className={`edit-input-${theme}`}
                  />
                </td>

                <td>
                  <input
                    type="text"
                    name="operator_phone"
                    value={editData.operator_phone || ""}
                    onChange={handleEditChange}
                    className={`edit-input-${theme}`}
                  />
                </td>

                <td>
                  <input
                    type="date"
                    name="defensive_driving_expiry"
                    value={
                      editData.defensive_driving_expiry
                        ? editData.defensive_driving_expiry.split("T")[0]
                        : ""
                    }
                    onChange={handleEditChange}
                    className={`edit-input-${theme}`}
                  />
                </td>

                <td>
                  <input
                    type="date"
                    name="first_aid_expiry"
                    value={
                      editData.first_aid_expiry
                        ? editData.first_aid_expiry.split("T")[0]
                        : ""
                    }
                    onChange={handleEditChange}
                    className={`edit-input-${theme}`}
                  />
                </td>

                <td>
                  <input
                    type="date"
                    name="dangerous_substances_expiry"
                    value={
                      editData.dangerous_substances_expiry
                        ? editData.dangerous_substances_expiry.split("T")[0]
                        : ""
                    }
                    onChange={handleEditChange}
                    className={`edit-input-${theme}`}
                  />
                </td>

                <td>
                  <input
                    type="date"
                    name="medical_exams_expiry"
                    value={
                      editData.medical_exams_expiry
                        ? editData.medical_exams_expiry.split("T")[0]
                        : ""
                    }
                    onChange={handleEditChange}
                    className={`edit-input-${theme}`}
                  />
                </td>

                <td>{formatDate(entity.updated_at)}</td>

                <td className="action-buttons sticky-column-right">
                  <button
                    className={`save-button-${theme}`}
                    onClick={handleSaveEdit}
                    disabled={updateEntityMutation.isLoading}
                  >
                    {updateEntityMutation.isLoading
                      ? "Guardando..."
                      : "Guardar"}
                  </button>
                  <button
                    className={`cancel-button-${theme}`}
                    onClick={handleCancelEdit}
                    disabled={updateEntityMutation.isLoading}
                  >
                    Cancelar
                  </button>
                </td>
              </>
            ) : (
              // Modo visualización para operador
              <>
                <td className="sticky-column selection-column">
                  <input
                    type="checkbox"
                    checked={selectedItems.includes(entity.operator_id_number)}
                    onChange={() =>
                      handleItemSelection(entity.operator_id_number)
                    }
                    className={`checkbox-${theme}`}
                  />
                </td>

                <td className="sticky-column">{entity.operator_id_number}</td>
                <td>{entity.operator_first_names || "N/A"}</td>
                <td>{entity.operator_last_names || "N/A"}</td>
                <td>{entity.operator_license || "N/A"}</td>
                <td>{entity.operator_license_category || "N/A"}</td>
                <td>{renderExpiryCell(entity.operator_license_expiry)}</td>
                <td>{entity.operator_arl || "N/A"}</td>
                <td>{entity.operator_eps || "N/A"}</td>
                <td>{entity.operator_phone || "N/A"}</td>
                <td>{renderExpiryCell(entity.defensive_driving_expiry)}</td>
                <td>{renderExpiryCell(entity.first_aid_expiry)}</td>
                <td>{renderExpiryCell(entity.dangerous_substances_expiry)}</td>
                <td>{renderExpiryCell(entity.medical_exams_expiry)}</td>
                <td>{formatDate(entity.updated_at)}</td>

                <td className="action-buttons sticky-column-right">
                  <button
                    className={`edit-button-${theme}`}
                    onClick={() => handleEdit(entity)}
                    disabled={!!isEditingId || deleteEntityMutation.isLoading}
                  >
                    Editar
                  </button>
                  <button
                    className={`delete-button-${theme}`}
                    onClick={() => handleDelete(entity.id)}
                    disabled={!!isEditingId || deleteEntityMutation.isLoading}
                  >
                    {deleteEntityMutation.isLoading &&
                    deleteEntityMutation.variables?.id === entity.id
                      ? "Eliminando..."
                      : "Eliminar"}
                  </button>
                </td>
              </>
            )}
          </tr>
        );
      } else {
        // Renderizar vehículo usando las nuevas funciones modulares
        return (
          <tr
            key={entity.id}
            className={isEditing ? `editing-row-${theme}` : ""}
          >
            {isEditing 
              ? renderVehicleEditMode(entity) 
              : renderVehicleViewMode(entity)}
          </tr>
        );
      }
    });
  };

  return (
    <div className={`inspection-list-container-${theme}`}>
      {/* Pestañas para cambiar entre vehículos y operadores */}
      <Tabs
        value={entityType}
        onChange={handleTypeChange}
        className={`entity-tabs-${theme}`}
        indicatorColor="primary"
        textColor="primary"
      >
        <Tab value="vehicles" label="Vehículos" />
        <Tab value="operators" label="Operadores" />
      </Tabs>

      <div className={`filters-container-${theme}`}>
        <form onSubmit={handleApplyFilters} className={`filter-form-${theme}`}>
          <div className={`filter-row-${theme}`}>
            {entityType === "vehicles" ? (
              <>
                <div className="filter-group">
                  <div className="filter-item">
                    <label htmlFor="plate">Placa</label>
                    <input
                      type="text"
                      id="plate"
                      name="plate"
                      value={filters.plate}
                      onChange={handleFilterChange}
                      className={`filter-input-${theme}`}
                      placeholder="Buscar por placa"
                      disabled={useRequiredPlates}
                    />
                  </div>

                  <div className="filter-item">
                    <label htmlFor="vehicleType">Tipo de Vehículo</label>
                    <select
                      id="vehicleType"
                      name="vehicleType"
                      value={filters.vehicleType}
                      onChange={handleFilterChange}
                      className={`filter-input-${theme}`}
                    >
                      <option value="">Todos los tipos</option>
                      <option value="TRACTOCAMION">Tractocamión</option>
                      <option value="DOBLE TROQUE">Doble Troque</option>
                      <option value="TURBO">Turbo</option>
                      <option value="CAMIONETA">Camioneta</option>
                      <option value="CAMA ALTA">Cama Alta</option>
                      <option value="OTROS">Otros</option>
                    </select>
                  </div>

                  <div className="filter-item checkbox-container">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={useRequiredPlates}
                          onChange={handleRequiredPlatesChange}
                          className={`checkbox-${theme}`}
                        />
                      }
                      label="Solo placas requeridas"
                      className={`checkbox-label-${theme}`}
                    />
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="filter-group">
                  <div className="filter-item">
                    <label htmlFor="operator_id">Documento</label>
                    <input
                      type="text"
                      id="operator_id"
                      name="operator_id"
                      value={filters.operator_id}
                      onChange={handleFilterChange}
                      className={`filter-input-${theme}`}
                      placeholder="Buscar por documento"
                    />
                  </div>

                  <div className="filter-item">
                    <label htmlFor="name">Nombre</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={filters.name}
                      onChange={handleFilterChange}
                      className={`filter-input-${theme}`}
                      placeholder="Buscar por nombre"
                    />
                  </div>
                </div>
              </>
            )}

            <div className="filter-actions">
              <button
                type="submit"
                className={`filter-button-${theme}`}
                disabled={
                  isLoading ||
                  updateEntityMutation.isLoading ||
                  deleteEntityMutation.isLoading
                }
              >
                {isLoading ? "Cargando..." : "Aplicar Filtros"}
              </button>

              <button
                ref={exportBtnRef}
                className={`export-button-${theme}`}
                onClick={handleExportClick}
                disabled={isLoading || exportToExcelMutation.isLoading}
                type="button"
              >
                {exportToExcelMutation.isLoading
                  ? "Exportando..."
                  : `Exportar ${
                      selectedItems.length > 0
                        ? `(${selectedItems.length} seleccionados)`
                        : "Todos"
                    }`}
              </button>
            </div>
          </div>
        </form>

        {/* Menú de opciones de exportación */}
        <Popover
          open={Boolean(exportMenuAnchor)}
          anchorEl={exportMenuAnchor}
          onClose={handleCloseExportMenu}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <div className={`export-menu-${theme}`}>
            <MenuItem onClick={() => handleExport("all")}>
              Exportar todos los registros
            </MenuItem>
            {entityType === "vehicles" && (
              <MenuItem onClick={() => handleExport("required")}>
                Exportar solo placas requeridas
              </MenuItem>
            )}
          </div>
        </Popover>
      </div>

      {isError && (
        <div className={`error-message-${theme}`}>
          {error?.message || "Error al cargar datos"}
        </div>
      )}

      <div className={`table-wrapper-${theme}`}>
        <div className={`table-container-${theme}`}>
          <table className={`inspections-table-${theme}`}>
            <thead>{renderTableHeader()}</thead>
            <tbody>{renderTableRows()}</tbody>
          </table>
        </div>
      </div>

      <div className={`pagination-${theme}`}>
        <button
          className={`pagination-button-${theme}`}
          onClick={() => handlePageChange(1)}
          disabled={pagination.page === 1 || isLoading}
        >
          &laquo;
        </button>
        <button
          className={`pagination-button-${theme}`}
          onClick={() => handlePageChange(pagination.page - 1)}
          disabled={pagination.page === 1 || isLoading}
        >
          &lt;
        </button>

        <span className={`page-info-${theme}`}>
          Página {pagination.page} de {paginationInfo.pages || 1}
        </span>

        <button
          className={`pagination-button-${theme}`}
          onClick={() => handlePageChange(pagination.page + 1)}
          disabled={pagination.page === paginationInfo.pages || isLoading}
        >
          &gt;
        </button>

        <button
          className={`pagination-button-${theme}`}
          onClick={() => handlePageChange(paginationInfo.pages)}
          disabled={pagination.page === paginationInfo.pages || isLoading}
        >
          &raquo;
        </button>

        <div className={`page-size-${theme}`}>
          <label>
            Mostrar:
            <select
              value={pagination.limit}
              onChange={(e) =>
                setPagination((prev) => ({
                  ...prev,
                  limit: Number(e.target.value),
                  page: 1,
                }))
              }
              className={`page-size-select-${theme}`}
              disabled={isLoading}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </label>
        </div>
      </div>
    </div>
  );
};

// Función auxiliar formatDate
const formatDate = (dateString) => {
  if (!dateString) return "N/A";

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return "Fecha inválida";
    }
    return date.toLocaleDateString("es-CO", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  } catch (e) {
    return "Fecha inválida";
  }
};

export default InspectionList;